import { observable, computed, action } from 'mobx';
import { UserFormStore, IUserFormValues } from '../domain/entities/UserFormStore';
import { LoginFormStore } from '../domain/entities/LoginFormStore';
import { Overlay } from '../domain/entities/Overlay';
import { IFacebookLogin, ILogin } from '../domain/entities/Login';
import { IErrorMessage } from '../views/common/ErrorPanel';
import { OnlineSignUpFormStore } from '../domain/entities/OnlineSignUpFormStore';
import { ICountry, RampSdApiSdk } from '../../api/RampSdApiSdk';
import { SignUpResponse, RampPgApiSdk } from '../../api/RampPgApiSdk';
import { diContainer, serviceNames } from '../../service-setup/diContainer';
import { UserType } from '../../constants/UserTypeEnum';
import { LoginType } from '../../constants/LoginTypeEnum';
import { SignInError } from '../../api/DataMappings';

export class UserRepository {
    @observable public userFormStore: UserFormStore;
    @observable public loginFormStore: LoginFormStore;
    @observable public overlay: Overlay;
    @observable public onlineSignUpFormStore: OnlineSignUpFormStore;
    @observable public login: object;
    @observable public userId: number;
    @observable public errorMessages: IErrorMessage[] | null;
    @observable public frontierErrorMessage: IErrorMessage | null;
    private rampSdApiSdk: RampSdApiSdk;
    private rampPgApiSdk: RampPgApiSdk;

    constructor(signUpFormInitialValues: IUserFormValues = {}) {
        this.userFormStore = new UserFormStore(signUpFormInitialValues);
        this.loginFormStore = new LoginFormStore();
        this.overlay = new Overlay();
        this.onlineSignUpFormStore = new OnlineSignUpFormStore();
        this.rampSdApiSdk = diContainer.get(serviceNames.rampSdApiSdk);
        this.rampPgApiSdk = diContainer.get(serviceNames.rampPgApiSdk);
    }

    @computed
    public get loginStatus() {
        return this.login;
    }

    @computed
    public get userData(): IUserFormValues {
        return this.userFormStore.values;
    }

    @computed
    public get loginData(): ILogin {
        return this.loginFormStore.values;
    }

    @action
    public setUserId = (userId: number) => {
        this.userId = userId;
    };

    @action
    public setErrorMessages = (errorMessages: IErrorMessage[]) => {
        this.errorMessages = errorMessages;
    };

    @action
    public clearErrorMessages = () => {
        this.errorMessages = null;
    };

    @action
    public setPosFrontierError = (frontierErrorMessage: IErrorMessage | null) => {
        this.frontierErrorMessage = frontierErrorMessage;
    };

    public getCountries = async (): Promise<ICountry[]> =>
        (await this.rampSdApiSdk.getAllCountries()) || [];

    public signUp = async (
        values: IUserFormValues
    ): Promise<SignUpResponse | Record<string, never>> => this.rampPgApiSdk.addUser(values);

    public updateUser = async (
        userId: number,
        values: IUserFormValues
    ): Promise<SignUpResponse | Record<string, never>> =>
        this.rampPgApiSdk.updateUser(userId, values);

    public completeSignUp = async (userId: number, password: string): Promise<boolean> =>
        this.rampPgApiSdk.patchPassword(userId, password);

    public signIn = async (
        formData: ILogin | IFacebookLogin,
        type?: LoginType
    ): Promise<SignInError | ''> => this.rampPgApiSdk.signIn(formData, type);

    @action
    public setUserType = (userType?: UserType) => {
        this.overlay.setUserType(userType);
    };

    @computed
    get isExistingUser(): boolean {
        return this.overlay.isExistingUser;
    }

    // set user type to undefined the close the overlay
    @action
    public resetOverlay() {
        this.setUserType(undefined);
    }
}
